import { isNetworkError } from "../api/errors";
import type { Endpoints } from "../api/endpoints";
import type { Sale, SaleRequest } from "../api/types";
import { uuid } from "../lib/ids";
import { db, type QueuedSale } from "./db";
import { setSyncState } from "./syncState";

/** Submitting a sale (§16.1). The real API is always tried first. Only a genuine network
 * failure (the request never reached a server) queues it on the device; business errors
 * (stock, credit limit, approval) are thrown to the screen and never queued. */
export type SubmitResult = { kind: "recorded"; sale: Sale } | { kind: "queued"; queued: QueuedSale };

export async function submitSale(api: Endpoints, req: SaleRequest, meta: { branchId: number; cashierName: string; total: number }): Promise<SubmitResult> {
  try {
    return { kind: "recorded", sale: await api.sales.create(req) };
  } catch (e) {
    if (!isNetworkError(e)) throw e;
    // An STK push can only happen live: never queue M-Pesa, or goods leave unpaid (review #2).
    if (req.payments.some((p) => p.method === "MPESA")) throw e;
  }
  const queued: QueuedSale = { id: uuid(), clientTxnId: req.clientTxnId, payload: { ...req, offline: true }, createdAt: new Date().toISOString(), status: "pending", lastError: null, ...meta };
  await (await db()).put("saleQueue", queued);
  await refreshQueueCounts();
  return { kind: "queued", queued };
}

export async function listQueue(): Promise<QueuedSale[]> {
  const all = await (await db()).getAll("saleQueue");
  return all.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** The branch whose queue the badges count (set by the sync agent for the signed-in user). */
let countBranch: number | null = null;
export const setQueueBranch = (branchId: number | null): void => {
  countBranch = branchId;
};

export async function refreshQueueCounts(): Promise<void> {
  try {
    const all = (await listQueue()).filter((q) => countBranch === null || q.branchId === countBranch);
    setSyncState({ pending: all.filter((q) => q.status === "pending").length, failed: all.filter((q) => q.status === "failed").length });
  } catch {
    /* storage unavailable: counts stay as they were */
  }
}
